import { OMRouteConfig } from './typing'
import { TAGS } from '../util/constant'
import { PATIENT_BUSINESS_TAGS, PROVIDER_BUSINESS_TAGS } from './questionTags'

// Comprehensive mapping from route.pageName to tracking tag constants.
// Keep this lean; prefer dynamic path parsing for survey section/category/question intros.
const pageNameToTag: Record<string, string> = {
  // Public / shared
  'Landing Page': TAGS.OM_HOMEPAGE,
  'About Us': TAGS.OM_ABOUT_US,
  'Contact Us': TAGS.OM_CONTACT_US,
  FAQ: TAGS.OM_FAQ,
  AccountSettings: TAGS.OM_SETTINGS,
  'Privacy Policy': TAGS.OM_PRIVACY_POLICY,
  'Terms of use': TAGS.OM_TERMS_OF_USE,

  // Patient experience core
  'Patient Dashboard': TAGS.OM_PA_LOGIN, // placeholder
  'Patient Home': TAGS.OM_PA_LOGIN, // placeholder
  'Patient Welcome': TAGS.OM_PA_EXPECTATION,
  'Patient Profile': TAGS.OM_PA_PC_SECTION_BACKGROUND,
  'Patient Activity': TAGS.OM_PATIENT_ACTIVITY,
  'Patient Minor': TAGS.OM_PA_MINOR,
  'Patient Paywall': TAGS.OM_PA_ACCESS_WALL,
  'Patient Privacy': TAGS.OM_PA_PRIVACY,
  'Patient Login': TAGS.OM_PA_LOGIN,
  'Patient Not Available': TAGS.OM_PA_ZIP_CODE_NOT_AVAILABLE,
  'Patient No Login': TAGS.OM_PA_LOGIN,
  'Patient -- Zip code entry': TAGS.OM_PA_BEGIN_SEARCH,
  'Disclaimer page': TAGS.OM_PA_DISCLAIMER,
  'Search Results': TAGS.OM_PA_MY_MATCHES,
  'Provider Profile View (Patient)': TAGS.OM_PA_MY_MATCHES,
  'Patient -- Expectation': TAGS.OM_PA_EXPECTATION,
  'Providers -- Expectation': TAGS.OM_CL_EXPECTATION,

  // Clinician experience core
  'Provider Home': TAGS.OM_CL_HOME,
  'Provider Welcome': TAGS.OM_CL_EXPECTATION,
  'Clinician Dashboard': TAGS.OM_CL_EXPECTATION,
  'Clinician Privacy': TAGS.OM_CL_PRIVACY,
  'Clinician Profile': TAGS.OM_CL_MY_PROFILE,
  'Clinician No Login': TAGS.OM_CL_LOGIN,
  'My Patients': TAGS.OM_CL_PC_COMM_STYLE_JOB_HEALTHY, // placeholder
  'Clinician My Matches': TAGS.OM_CL_PC_CARE_APPROACH, // placeholder
  'Clinician Not Authorized': TAGS.OM_CL_LOGIN,
  'Clinician Login': TAGS.OM_CL_LOGIN,
  Clinicians: TAGS.OM_CL_PC_BACKGROUND,
  'Add Clinician': TAGS.OM_CL_PC_BACKGROUND,
  'Clinician Details': TAGS.OM_CL_PC_BACKGROUND,
  Organizations: TAGS.OM_CL_PC_BACKGROUND,
  'Add Organization': TAGS.OM_CL_PC_BACKGROUND,
  'Organization Details': TAGS.OM_CL_PC_BACKGROUND,
  'Service Locations': TAGS.OM_CL_PC_BACKGROUND,
  'Add Location': TAGS.OM_CL_PC_BACKGROUND,
  'Location Details': TAGS.OM_CL_PC_BACKGROUND,
  Admin: TAGS.OM_CL_PC_BACKGROUND,
  'Admin Home': TAGS.OM_CL_PC_BACKGROUND,

  // Generic survey structural placeholders (patient defaults). Provider disambiguated dynamically.
  'Survey Intro': TAGS.OM_PA_SURVEY_REVIEW,
  'Survey Category': TAGS.OM_PA_SURVEY_REVIEW,
  'Survey Question': TAGS.OM_PA_SURVEY_REVIEW, // provider path handled in deriveTag
  'Survey Review': TAGS.OM_PA_SURVEY_REVIEW, // disambiguated in deriveTag
  'Survey Feedback': TAGS.OM_PA_SURVEY_FEEDBACK,

  // Provider dashboard & access issue explicit tags (avoid raw pageName in analytics)
  'Provider Dashboard': TAGS.OM_CL_DASHBOARD,
  'Clinician- Access Issue': TAGS.OM_CL_LOGIN,

  // Fallbacks / ambiguous
  'Not Available': TAGS.OM_PA_ZIP_CODE_NOT_AVAILABLE,
  'Not Found': TAGS.OM_PA_ZIP_CODE_NOT_AVAILABLE,
}

// Resolve tag;
// - Disambiguate patient vs provider survey structural pages by full original pathname.
// - Dynamically map section (c1..c4) for Survey Section / Intro / Category routes.
// - Provide provider placeholder for Survey Question until override sets businessId tag.
const deriveTag = (
  route: OMRouteConfig,
  currentPathname: string,
  fullPathname: string
): string | undefined => {
  const effectiveFullPath = fullPathname || currentPathname

  if (!route.pageName) {
    if (effectiveFullPath === '/about-us') return TAGS.OM_ABOUT_US
    return undefined
  }

  if (effectiveFullPath === '/about-us') return TAGS.OM_ABOUT_US

  // Survey review disambiguation
  if (route.pageName === 'Survey Review') {
    if (
      effectiveFullPath.startsWith('/providers') ||
      effectiveFullPath.startsWith('/clinician')
    ) {
      return TAGS.OM_CL_MANAGE_RESPONSES
    }
    if (effectiveFullPath.startsWith('/patients')) {
      return TAGS.OM_PA_SURVEY_REVIEW
    }
  }

  // Survey feedback disambiguation
  if (route.pageName === 'Survey Feedback') {
    if (effectiveFullPath.startsWith('/providers'))
      return TAGS.OM_CL_SURVEY_FEEDBACK
    if (effectiveFullPath.startsWith('/patients'))
      return TAGS.OM_PA_SURVEY_FEEDBACK
  }

  // Provider survey question placeholder until Question component override fires
  if (route.pageName === 'Survey Question') {
    if (effectiveFullPath.startsWith('/providers'))
      return TAGS.OM_CL_SURVEY_REVIEW
    if (effectiveFullPath.startsWith('/patients'))
      return TAGS.OM_PA_SURVEY_REVIEW
  }

  // Dynamic section / intro / category mapping using full path (supports singular roots 'patient'/'provider')
  if (
    route.pageName === 'Survey Section' ||
    route.pageName === 'Survey Intro' ||
    route.pageName === 'Survey Category'
  ) {
    const parts = effectiveFullPath.split('/').filter(Boolean)
    const surveyIndex = parts.indexOf('survey')

    if (surveyIndex !== -1 && parts.length > surveyIndex + 1) {
      const rawSectionId = parts[surveyIndex + 1]
      const sectionId = rawSectionId.toLowerCase()
      const isPatient = parts[0] === 'patients' || parts[0] === 'patient'
      const isProvider = parts[0] === 'providers' || parts[0] === 'provider'

      if (isPatient) {
        const tag = PATIENT_BUSINESS_TAGS[sectionId]
        if (tag) {
          return tag
        }
      }
      if (isProvider) {
        const tag = PROVIDER_BUSINESS_TAGS[sectionId]
        if (tag) {
          return tag
        }
      }
    }
  }

  const fallbackTag = pageNameToTag[route.pageName]

  return fallbackTag
}

export const findByRoute = (
  routes: OMRouteConfig[],
  pathname: string,
  fullPathname: string = pathname
): OMRouteConfig | null => {
  // First pass: try to match exact/specific paths (non-dynamic routes)
  for (const route of routes) {
    const isRootMatch = route.path === '' && pathname === '/'

    // For specific path matching, ensure it's either exact or followed by a slash
    let isSpecificMatch = false
    if (route.path && !route.path.startsWith(':')) {
      const routePath = '/' + route.path
      isSpecificMatch =
        pathname === routePath || pathname.startsWith(routePath + '/')
    }

    // Handle index/layout routes with empty path that have children (pass-through)
    const isEmptyPathWithChildren =
      route.path === '' && route.children && pathname !== '/'

    if (isRootMatch || isSpecificMatch) {
      if (route.children) {
        const nextSlash = pathname.indexOf('/', pathname.indexOf('/') + 1)
        const childPathname =
          nextSlash !== -1 ? pathname.substring(nextSlash) : pathname
        const matchedChildRoute = findByRoute(
          route.children,
          `${childPathname}`,
          fullPathname
        )
        if (matchedChildRoute) {
          return matchedChildRoute
        }
      }
      return {
        ...route,
        analyticsTag: deriveTag(route, pathname, fullPathname),
      }
    } else if (isEmptyPathWithChildren && route.children) {
      // For layout routes with empty path, search children directly with same pathname
      const matchedChildRoute = findByRoute(
        route.children,
        pathname,
        fullPathname
      )
      if (matchedChildRoute) {
        return matchedChildRoute
      }
    }
  }

  // Second pass: try dynamic parameter routes (like :sectionId)
  for (const route of routes) {
    const isDynamicMatch = route.path && route.path.startsWith(':')

    if (isDynamicMatch) {
      if (route.children) {
        const nextSlash = pathname.indexOf('/', pathname.indexOf('/') + 1)
        const childPathname =
          nextSlash !== -1 ? pathname.substring(nextSlash) : pathname
        const matchedChildRoute = findByRoute(
          route.children,
          `${childPathname}`,
          fullPathname
        )
        if (matchedChildRoute) {
          return matchedChildRoute
        }
      }
      return {
        ...route,
        analyticsTag: deriveTag(route, pathname, fullPathname),
      }
    }
  }

  return null
}

// Convenience: get resolved analytics tag directly from pathname.
export const getTagByPathname = (
  routes: OMRouteConfig[],
  pathname: string
): string | undefined => {
  const found = findByRoute(routes, pathname)
  return found?.analyticsTag
}
